import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE from "../config/api";
import { getInitials, resolveImageUrl } from "../utils/siteHelpers";
import SiteLoadingState from "../components/site/SiteLoadingState";
import SiteErrorState from "../components/site/SiteErrorState";
import SiteEmptyState from "../components/site/SiteEmptyState";
import { getThemeVars } from "../utils/businessThemes";

/*
  U13 — restyled to follow the structure of the reference political
  portfolio site (full-height hero -> sticky dark nav below it ->
  About w/ quick-facts side panel -> Achievements cards -> horizontal
  scrolling Timeline track -> Gallery -> Video -> footer/contact),
  instead of the plainer single-column layout this had before.
  Skipped from the reference: the "activities" quick-facts feed and
  hero stat counters (years in politics / terms / approval) — neither
  has a matching field in political_site_data, so nothing to show
  there without inventing data.
*/

function extractYouTubeId(url) {
    if (!url) return "";
    const m = String(url).match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
    return m ? m[1] : "";
}

function PwImg({ src, alt, className = "", icon = "bi-image", loading = "lazy" }) {
    const [ok, setOk] = useState(true);
    useEffect(() => { setOk(true); }, [src]);
    if (src && ok) {
        return <img src={src} alt={alt} loading={loading} className={className} onError={() => setOk(false)} />;
    }
    return (
        <div className={`pw-img-fallback ${className}`} role="img" aria-label={alt || "Image unavailable"}>
            <i className={`bi ${icon}`} aria-hidden="true" />
        </div>
    );
}

export default function PoliticalWebsite() {
    const { domain } = useParams();
    const [real, setReal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [fetchToken, setFetchToken] = useState(0);
    const [photoOk, setPhotoOk] = useState(true);
    const [coverOk, setCoverOk] = useState(true);
    const [navOpen, setNavOpen] = useState(false);

    useEffect(() => {
        if (!domain) { setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setLoadError(false);
        fetch(`${API_BASE}/api/get_political_site.php?domain=${encodeURIComponent(domain)}`)
            .then((r) => r.json())
            .then((res) => { if (!cancelled && res.success && res.data) setReal(res.data); })
            .catch(() => { if (!cancelled) setLoadError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [domain, fetchToken]);

    if (loading) return <SiteLoadingState theme="business" label="Loading site…" />;
    if (loadError) return <SiteErrorState theme="business" onRetry={() => setFetchToken((n) => n + 1)} />;

    const resolveImg = (v) => (v ? resolveImageUrl(API_BASE, v) : v);
    const d = real || {};
    const name = d.name || "";
    const initials = getInitials(name, "??");
    const photo = resolveImg(d.photo || "");
    const cover = resolveImg(d.cover || "");
    const gallery = d.gallery || [];
    const achievements = d.achievements || [];
    const timeline = d.timeline || [];
    const ytId = extractYouTubeId(d.youtube_link);
    const theme = getThemeVars(d.theme_color);
    const isEmpty = !name && !d.about && achievements.length === 0 && timeline.length === 0 && gallery.length === 0;

    const quickFacts = [
        d.position_title && { icon: "bi-briefcase-fill", label: "Position", value: d.position_title },
        d.party && { icon: "bi-flag-fill", label: "Party", value: d.party },
        d.address && { icon: "bi-geo-alt-fill", label: "Constituency", value: d.address },
        d.phone && { icon: "bi-telephone-fill", label: "Phone", value: d.phone },
        d.email && { icon: "bi-envelope-fill", label: "Email", value: d.email },
    ].filter(Boolean);

    const cssVars = {
        "--brand": theme.brand, "--brand-2": theme.brand2,
        "--accent": theme.accent, "--accent-d": theme.accentD,
    };

    const navLinks = [
        d.about && { id: "about", label: "About" },
        achievements.length > 0 && { id: "achievements", label: "Achievements" },
        timeline.length > 0 && { id: "timeline", label: "Timeline" },
        gallery.length > 0 && { id: "gallery", label: "Gallery" },
        ytId && { id: "video", label: "Video" },
        { id: "contact", label: "Contact" },
    ].filter(Boolean);

    const scrollToId = (id) => {
        setNavOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="pw-page" style={cssVars}>
            <style>{`
                .pw-page{ font-family:'Inter',system-ui,sans-serif; color:#1a1f27; background:#f7f8fa; min-height:100vh; }

                /* ---------- Hero (full height, like the reference "portfolio-header") ---------- */
                .pw-hero{ position:relative; min-height:100vh; display:flex; align-items:center;
                    background:${cover && coverOk
                        ? `linear-gradient(rgba(6,10,18,.72), rgba(6,10,18,.58)), url(${JSON.stringify(cover)})`
                        : `linear-gradient(135deg, var(--brand) 0%, var(--brand-2) 55%, #0b1018 100%)`};
                    background-size:cover; background-position:center; color:#fff; }
                .pw-hero-inner{ position:relative; z-index:1; max-width:1140px; margin:0 auto; padding:100px 24px 60px; display:flex; gap:56px; align-items:center; flex-wrap:wrap; width:100%; }
                .pw-photo-wrap{ position:relative; flex-shrink:0; }
                .pw-photo{ width:280px; height:340px; border-radius:22px; object-fit:cover; border:7px solid rgba(255,255,255,.92); box-shadow:0 30px 70px rgba(0,0,0,.45);
                    transform:perspective(1000px) rotateY(-8deg); transition:transform .35s ease; }
                .pw-photo-wrap:hover .pw-photo{ transform:perspective(1000px) rotateY(0deg); }
                .pw-img-fallback{ background:linear-gradient(135deg,var(--brand-2),var(--brand)); display:flex; align-items:center; justify-content:center; color:#fff; font-weight:800; }
                .pw-photo.pw-img-fallback{ font-size:64px; }
                .pw-party-ribbon{ position:absolute; bottom:16px; right:-18px; background:linear-gradient(135deg, var(--accent), var(--accent-d)); color:#fff; padding:9px 22px 9px 14px; border-radius:12px; font-weight:700; font-size:1rem; box-shadow:0 10px 24px rgba(0,0,0,.3); transform:rotate(-4deg); border:3px solid #fff; display:flex; align-items:center; gap:8px; max-width:220px; }
                .pw-name{ font-size:3rem; font-weight:800; margin:0 0 14px; line-height:1.1; background:linear-gradient(135deg,#fff 0%, rgba(255,255,255,.85) 100%); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; }
                .pw-tagline{ opacity:.92; margin:0 0 20px; font-size:1.2rem; font-weight:500; }
                .pw-badges{ display:flex; gap:10px; flex-wrap:wrap; }
                .pw-badge{ background:rgba(255,255,255,.14); border:1px solid rgba(255,255,255,.35); border-radius:999px; padding:7px 16px; font-size:14px; font-weight:600; backdrop-filter:blur(2px); }
                @media (max-width:900px){ .pw-hero-inner{ padding:90px 20px 40px; gap:32px; } .pw-photo{ width:190px; height:240px; } .pw-name{ font-size:2.1rem; } .pw-party-ribbon{ right:-10px; font-size:.85rem; padding:7px 16px 7px 10px; } }

                /* ---------- Nav — below the hero in the DOM, sticks to top once scrolled to (matches reference) ---------- */
                .pw-nav{ position:fixed; top:0; left:0; right:0; width:100%; z-index:20; background:var(--brand); box-shadow:0 5px 20px rgba(0,0,0,.15); }
                .pw-nav-inner{ max-width:1140px; margin:0 auto; padding:12px 24px; display:flex; align-items:center; justify-content:space-between; gap:16px; }
                .pw-nav-brand{ display:flex; align-items:center; gap:10px; min-width:0; }
                .pw-nav-thumb{ width:36px; height:36px; border-radius:9px; object-fit:cover; flex-shrink:0; }
                .pw-nav-thumb.pw-img-fallback{ font-size:14px; }
                .pw-nav-name{ font-weight:800; font-size:15px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; color:#fff; }
                .pw-nav-links{ display:flex; align-items:center; gap:4px; }
                .pw-nav-link{ background:none; border:none; padding:9px 14px; font-size:14px; font-weight:600; color:rgba(255,255,255,.85); border-radius:8px; cursor:pointer; white-space:nowrap; }
                .pw-nav-link:hover{ background:rgba(255,255,255,.12); color:#fff; }
                .pw-nav-toggle{ display:none; background:none; border:none; font-size:22px; color:#fff; cursor:pointer; }
                @media (max-width:780px){
                    .pw-nav-links{ position:absolute; top:100%; left:0; right:0; background:var(--brand); flex-direction:column; align-items:stretch; gap:0; padding:6px; box-shadow:0 8px 16px rgba(0,0,0,.2); display:none; }
                    .pw-nav-links.open{ display:flex; }
                    .pw-nav-link{ text-align:left; width:100%; border-radius:6px; padding:12px; }
                    .pw-nav-toggle{ display:block; }
                }

                .pw-anchor{ scroll-margin-top:66px; }
                .pw-section{ max-width:1140px; margin:0 auto; padding:56px 24px; }
                .pw-section h2{ font-size:1.6rem; font-weight:800; margin-bottom:24px; color:var(--brand); }

                /* ---------- About + quick facts ---------- */
                .pw-about-grid{ display:grid; grid-template-columns:2fr 1fr; gap:36px; align-items:start; }
                .pw-about{ font-size:1.05rem; line-height:1.8; white-space:pre-wrap; }
                .pw-facts{ background:#fff; border-radius:14px; padding:20px; box-shadow:0 4px 16px rgba(0,0,0,.06); }
                .pw-fact-item{ display:flex; align-items:center; gap:14px; padding:14px 0; border-bottom:1px solid #eef0f3; }
                .pw-fact-item:last-child{ border-bottom:none; padding-bottom:0; }
                .pw-fact-icon{ width:40px; height:40px; flex-shrink:0; border-radius:10px; background:linear-gradient(135deg,var(--brand),var(--brand-2)); display:flex; align-items:center; justify-content:center; color:#fff; }
                .pw-fact-label{ font-size:12px; color:#8a94a3; }
                .pw-fact-value{ font-weight:700; font-size:14px; color:#1f2937; word-break:break-word; }
                @media (max-width:780px){ .pw-about-grid{ grid-template-columns:1fr; } }

                /* ---------- Achievements ---------- */
                .pw-ach-grid{ display:grid; grid-template-columns:repeat(auto-fit,minmax(230px,1fr)); gap:20px; }
                .pw-ach-card{ background:#fff; border-radius:14px; padding:22px 18px; text-align:center; box-shadow:0 4px 16px rgba(0,0,0,.06); position:relative; overflow:hidden; transition:transform .25s ease, box-shadow .25s ease; }
                .pw-ach-card::before{ content:""; position:absolute; top:0; left:0; right:0; height:5px; background:linear-gradient(135deg,var(--brand),var(--brand-2)); }
                .pw-ach-card:hover{ transform:translateY(-8px); box-shadow:0 14px 34px rgba(0,0,0,.12); }
                .pw-ach-icon{ width:50px; height:50px; margin:0 auto 14px; border-radius:50%; background:linear-gradient(135deg,var(--accent),var(--accent-d)); display:flex; align-items:center; justify-content:center; color:#fff; font-size:1.2rem; }
                .pw-ach-year{ color:var(--accent-d); font-weight:700; font-size:13px; margin-bottom:6px; }
                .pw-ach-title{ font-weight:700; margin-bottom:6px; }
                .pw-ach-desc{ font-size:14px; color:#5b6472; }

                /* ---------- Timeline — horizontal scrolling track ---------- */
                .pw-tl-scroll{ overflow-x:auto; padding-bottom:10px; margin:0 -24px; padding-left:24px; padding-right:24px; }
                .pw-tl-track{ position:relative; display:flex; gap:24px; min-width:min-content; padding-top:24px; }
                .pw-tl-track::before{ content:""; position:absolute; top:24px; left:8px; right:8px; height:3px; background:linear-gradient(90deg,var(--brand),var(--brand-2)); }
                .pw-tl-item{ position:relative; min-width:240px; max-width:240px; flex-shrink:0; transition:transform .25s ease; }
                .pw-tl-item:hover{ transform:translateY(-6px); }
                .pw-tl-dot{ position:absolute; top:10px; left:0; width:14px; height:14px; border-radius:50%; background:#fff; border:3px solid var(--brand); }
                .pw-tl-card{ background:#fff; border-radius:14px; padding:18px; margin-top:26px; box-shadow:0 4px 16px rgba(0,0,0,.06); }
                .pw-tl-year{ display:inline-block; background:linear-gradient(135deg,var(--brand),var(--brand-2)); color:#fff; padding:5px 13px; border-radius:50px; font-weight:700; font-size:.82rem; margin-bottom:10px; }
                .pw-tl-title{ font-weight:700; margin-bottom:6px; font-size:1rem; }
                .pw-tl-desc{ font-size:.85rem; line-height:1.5; color:#5b6472; }

                .pw-gallery{ display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:16px; }
                .pw-gallery img,.pw-gallery .pw-img-fallback{ width:100%; height:180px; border-radius:12px; object-fit:cover; box-shadow:0 4px 12px rgba(0,0,0,.08); }
                .pw-video{ position:relative; padding-top:56.25%; border-radius:14px; overflow:hidden; box-shadow:0 6px 20px rgba(0,0,0,.1); }
                .pw-video iframe{ position:absolute; inset:0; width:100%; height:100%; border:0; }

                /* ---------- Footer ---------- */
                .pw-footer{ background:var(--brand); color:#e7ebf0; }
                .pw-footer-inner{ max-width:1140px; margin:0 auto; padding:44px 24px 22px; display:grid; grid-template-columns:1.3fr 1fr; gap:28px; }
                .pw-footer-brand{ display:flex; align-items:center; gap:10px; font-weight:800; font-size:18px; margin-bottom:10px; }
                .pw-footer-brand img,.pw-footer-brand .pw-img-fallback{ width:36px; height:36px; border-radius:9px; object-fit:cover; }
                .pw-footer-desc{ font-size:14px; opacity:.85; line-height:1.6; max-width:440px; }
                .pw-footer h4{ font-size:13px; text-transform:uppercase; letter-spacing:.06em; opacity:.7; margin-bottom:14px; }
                .pw-contact-line{ display:flex; align-items:center; gap:9px; margin-bottom:10px; font-size:14px; }
                .pw-socials{ display:flex; gap:10px; margin-top:12px; }
                .pw-social-btn{ width:38px; height:38px; border-radius:50%; background:rgba(255,255,255,.12); display:flex; align-items:center; justify-content:center; color:#fff; text-decoration:none; }
                .pw-social-btn:hover{ background:var(--accent); }
                .pw-footer-bottom{ text-align:center; padding:16px 20px; border-top:1px solid rgba(255,255,255,.14); font-size:13px; opacity:.7; }
                @media (max-width:640px){ .pw-footer-inner{ grid-template-columns:1fr; } }
            `}</style>

            {/* ---------- Hero ---------- */}
            <div className="pw-hero">
                {cover && <img src={cover} alt="" style={{ display: "none" }} onError={() => setCoverOk(false)} />}
                <div className="pw-hero-inner">
                    <div className="pw-photo-wrap">
                        {photo && photoOk
                            ? <img src={photo} alt={name} className="pw-photo" onError={() => setPhotoOk(false)} />
                            : <div className="pw-photo pw-img-fallback" aria-hidden="true">{initials}</div>}
                        {d.party && <div className="pw-party-ribbon"><i className="bi bi-flag-fill" />{d.party}</div>}
                    </div>
                    <div>
                        <h1 className="pw-name">{name || "Political Website"}</h1>
                        {d.tagline && <p className="pw-tagline">{d.tagline}</p>}
                        <div className="pw-badges">
                            {d.position_title && <span className="pw-badge"><i className="bi bi-briefcase-fill me-1" />{d.position_title}</span>}
                            {d.address && <span className="pw-badge"><i className="bi bi-geo-alt-fill me-1" />{d.address}</span>}
                        </div>
                    </div>
                </div>
            </div>

            {/* ---------- Nav (after hero, sticks on scroll) ---------- */}
            <nav className="pw-nav">
                <div className="pw-nav-inner">
                    <div className="pw-nav-brand">
                        {photo && photoOk
                            ? <img src={photo} alt={name} className="pw-nav-thumb" onError={() => setPhotoOk(false)} />
                            : <div className="pw-nav-thumb pw-img-fallback" aria-hidden="true">{initials}</div>}
                        <span className="pw-nav-name">{name || "Political Website"}</span>
                    </div>
                    <button className="pw-nav-toggle" onClick={() => setNavOpen((v) => !v)} aria-label="Toggle menu">
                        <i className={`bi ${navOpen ? "bi-x-lg" : "bi-list"}`} />
                    </button>
                    <div className={`pw-nav-links ${navOpen ? "open" : ""}`}>
                        {navLinks.map((l) => (
                            <button key={l.id} className="pw-nav-link" onClick={() => scrollToId(l.id)}>{l.label}</button>
                        ))}
                    </div>
                </div>
            </nav>

            {isEmpty ? (
                <div className="pw-section">
                    <SiteEmptyState icon="bi-megaphone" title="This site hasn't been set up yet" message="Check back soon." />
                </div>
            ) : (
                <>
                    {(d.about || quickFacts.length > 0) && (
                        <div className="pw-section pw-anchor" id="about">
                            <h2>About</h2>
                            <div className="pw-about-grid">
                                <div className="pw-about">{d.about}</div>
                                {quickFacts.length > 0 && (
                                    <div className="pw-facts">
                                        {quickFacts.map((f, i) => (
                                            <div className="pw-fact-item" key={i}>
                                                <div className="pw-fact-icon"><i className={`bi ${f.icon}`} /></div>
                                                <div>
                                                    <div className="pw-fact-label">{f.label}</div>
                                                    <div className="pw-fact-value">{f.value}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {achievements.length > 0 && (
                        <div className="pw-section pw-anchor" id="achievements">
                            <h2>Achievements</h2>
                            <div className="pw-ach-grid">
                                {achievements.map((a, i) => (
                                    <div className="pw-ach-card" key={i}>
                                        <div className="pw-ach-icon"><i className="bi bi-award-fill" /></div>
                                        {a.year && <div className="pw-ach-year">{a.year}</div>}
                                        <div className="pw-ach-title">{a.title}</div>
                                        {a.description && <div className="pw-ach-desc">{a.description}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {timeline.length > 0 && (
                        <div className="pw-section pw-anchor" id="timeline">
                            <h2>Career Timeline</h2>
                            <div className="pw-tl-scroll">
                                <div className="pw-tl-track">
                                    {timeline.map((t, i) => (
                                        <div className="pw-tl-item" key={i}>
                                            <div className="pw-tl-dot" />
                                            <div className="pw-tl-card">
                                                {t.year && <div className="pw-tl-year">{t.year}</div>}
                                                <div className="pw-tl-title">{t.title}</div>
                                                {t.description && <div className="pw-tl-desc">{t.description}</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {gallery.length > 0 && (
                        <div className="pw-section pw-anchor" id="gallery">
                            <h2>Gallery</h2>
                            <div className="pw-gallery">
                                {gallery.map((g, i) => (
                                    <PwImg key={i} src={resolveImg(g)} alt={`${name} photo ${i + 1}`} icon="bi-image" />
                                ))}
                            </div>
                        </div>
                    )}

                    {ytId && (
                        <div className="pw-section pw-anchor" id="video">
                            <h2>Video</h2>
                            <div className="pw-video">
                                <iframe src={`https://www.youtube.com/embed/${ytId}`} title={`${name} video`} allowFullScreen />
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* ---------- Footer ---------- */}
            <div className="pw-footer pw-anchor" id="contact">
                <div className="pw-footer-inner">
                    <div>
                        <div className="pw-footer-brand">
                            {photo && photoOk
                                ? <img src={photo} alt={name} />
                                : <div className="pw-img-fallback" aria-hidden="true">{initials}</div>}
                            {name || "Political Website"}
                        </div>
                        {(d.tagline || d.about) && <p className="pw-footer-desc">{d.tagline || (d.about || "").slice(0, 160)}</p>}
                        <div className="pw-socials">
                            {d.linkfacebook && <a className="pw-social-btn" href={d.linkfacebook} target="_blank" rel="noreferrer"><i className="bi bi-facebook" /></a>}
                            {d.linkinstagram && <a className="pw-social-btn" href={d.linkinstagram} target="_blank" rel="noreferrer"><i className="bi bi-instagram" /></a>}
                            {d.linktwitter && <a className="pw-social-btn" href={d.linktwitter} target="_blank" rel="noreferrer"><i className="bi bi-twitter-x" /></a>}
                        </div>
                    </div>
                    <div>
                        <h4>Contact</h4>
                        {d.phone && <div className="pw-contact-line"><i className="bi bi-telephone-fill" />{d.phone}</div>}
                        {d.email && <div className="pw-contact-line"><i className="bi bi-envelope-fill" />{d.email}</div>}
                        {d.website && <div className="pw-contact-line"><i className="bi bi-globe" />{d.website}</div>}
                        {d.address && <div className="pw-contact-line"><i className="bi bi-geo-alt-fill" />{d.address}</div>}
                    </div>
                </div>
                <div className="pw-footer-bottom">© {new Date().getFullYear()} {name || "Political Website"}. All rights reserved.</div>
            </div>
        </div>
    );
}